import React, { useMemo, useState } from 'react';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { useBilling } from '../../contexts/BillingContext';
import { CatalogueItem } from '../../types/billing';
import { formatDateTime, formatMoney, fromN, toN } from '../../utils/billingMoney';
import { exportCsv } from '../../utils/hrExport';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { ItemFormModal } from './ItemFormModal';
import { StockAdjustModal } from './StockAdjustModal';
type StockState = 'In stock' | 'Low' | 'Out of stock' | 'Service';
type Row = {
  item: CatalogueItem;
  onHand: number | null;
  state: StockState;
  value: number;
};
const tone: Record<StockState, string> = {
  'In stock': 'bg-emerald-50 text-emerald-800',
  Low: 'bg-amber-50 text-amber-800',
  'Out of stock': 'bg-red-50 text-red-800',
  Service: 'bg-slate-100 text-slate-600'
};

/** Inventory of every billing item: stock on hand, reorder alerts, value at cost, and movement history. */
export function BillingInventory() {
  const {
    items,
    stockMoves,
    config,
    can
  } = useBilling();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'stock' | 'low' | 'out' | 'service'>('all');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CatalogueItem | null>(null);
  const [adjust, setAdjust] = useState<{
    item: CatalogueItem;
    mode: 'Received' | 'Adjustment';
  } | null>(null);
  const [history, setHistory] = useState<CatalogueItem | null>(null);
  const [message, setMessage] = useState('');
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const manage = can('manageCatalogue');
  const rows: Row[] = useMemo(() => items.filter(i => i.status === 'Active').map(item => {
    if (item.kind !== 'Stock') return {
      item,
      onHand: null,
      state: 'Service' as const,
      value: 0
    };
    const onHand = Number(item.quantityOnHand) || 0;
    const reorder = Number(item.reorderLevel) || 0;
    const state: StockState = onHand <= 0 ? 'Out of stock' : onHand <= reorder ? 'Low' : 'In stock';
    return {
      item,
      onHand,
      state,
      value: fromN(onHand * toN(item.costPrice ?? 0))
    };
  }), [items]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => filter === 'all' || filter === 'stock' && r.onHand !== null || filter === 'low' && r.state === 'Low' || filter === 'out' && r.state === 'Out of stock' || filter === 'service' && r.state === 'Service').filter(r => !q || [r.item.code, r.item.name, r.item.description, r.item.supplier, r.item.location].some(v => v?.toLowerCase().includes(q))).sort((a, b) => a.item.name.localeCompare(b.item.name));
  }, [rows, query, filter]);
  const stockRows = rows.filter(r => r.onHand !== null);
  const totalValue = fromN(stockRows.reduce((s, r) => s + toN(r.value), 0));
  const low = rows.filter(r => r.state === 'Low').length;
  const out = rows.filter(r => r.state === 'Out of stock').length;
  const itemMoves = history ? stockMoves.filter(m => m.itemId === history.id).sort((a, b) => b.at.localeCompare(a.at)) : [];
  return <div className="space-y-3" data-id="element-2846" data-mp="17w9lwz9b1e0k">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-2847" data-mp="134rkka3ddbf2">
        <div data-id="element-2848" data-mp="132n7p2nht22n">
          <h1 className="text-xl font-bold text-slate-900" data-id="element-2849" data-mp="132eutarddwrg">Inventory</h1>
          <p className="text-sm text-slate-500" data-id="element-2850" data-mp="y5enkwisanyr">Every item you add is listed here. Stock items are counted; cash sales reduce them automatically.</p>
        </div>
        <div className="flex gap-2" data-id="element-2851" data-mp="o77k9ghf44f8">
          {can('exportData') && <Button size="sm" variant="outline" onClick={() => exportCsv('Billing-inventory', [{
          key: 'code',
          label: 'Item'
        }, {
          key: 'name',
          label: 'Name'
        }, {
          key: 'type',
          label: 'Type'
        }, {
          key: 'onHand',
          label: 'On hand',
          align: 'right'
        }, {
          key: 'unit',
          label: 'Unit'
        }, {
          key: 'reorder',
          label: 'Reorder level',
          align: 'right'
        }, {
          key: 'cost',
          label: 'Cost',
          align: 'right'
        }, {
          key: 'price',
          label: 'Price',
          align: 'right'
        }, {
          key: 'value',
          label: 'Value at cost',
          align: 'right'
        }, {
          key: 'state',
          label: 'Status'
        }], shown.map(r => ({
          code: r.item.code,
          name: r.item.name,
          type: r.state === 'Service' ? 'Service' : 'Stock',
          onHand: r.onHand ?? '',
          unit: r.item.unit ?? '',
          reorder: r.item.reorderLevel ?? '',
          cost: r.item.costPrice ?? '',
          price: r.item.unitPrice,
          value: r.onHand === null ? '' : r.value,
          state: r.state
        })))} data-id="element-2852" data-mp="o9tqncskodvv">Export CSV</Button>}
          {manage && <Button size="sm" onClick={() => setAdding(true)} data-id="element-2853" data-mp="sa4y0861sbnt"><PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" data-id="element-2854" data-mp="sawurmbdsffo" />Add New Item</Button>}
        </div>
      </div>

      <dl className="flex flex-wrap gap-x-10 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3" data-id="element-2855" data-mp="ekbb3ve3s1gq">
        <div data-id="element-2856" data-mp="ikld6wyry7qb"><dt className="text-xs text-slate-500" data-id="element-2857" data-mp="ii8z4uw50hij">Stock value at cost</dt><dd className="text-2xl font-bold tabular-nums text-slate-900" data-id="element-2858" data-mp="4llpbrj35wv7">{fmt(totalValue)}</dd></div>
        <div data-id="element-2859" data-mp="9ilv01c0wxls"><dt className="text-xs text-slate-500" data-id="element-2860" data-mp="9ilzadj23jrl">Items</dt><dd className="text-lg font-semibold tabular-nums" data-id="element-2861" data-mp="9lhu3szlxsbt">{rows.length} <span className="text-sm font-normal text-slate-500" data-id="element-2862" data-mp="3m3rlsbqf16kn">({stockRows.length} stocked)</span></dd></div>
        <button type="button" onClick={() => setFilter('low')} className="text-left" data-id="element-2863" data-mp="3h5zjcns5a227"><dt className="text-xs text-slate-500" data-id="element-2864" data-mp="3w2gr7514kyk0">Low stock</dt><dd className={`text-lg font-semibold tabular-nums ${low ? 'text-amber-700' : ''}`} data-id="element-2865" data-mp="3r2cbhh81k7uu">{low}</dd></button>
        <button type="button" onClick={() => setFilter('out')} className="text-left" data-id="element-2866" data-mp="8wg3j6m8lqhk"><dt className="text-xs text-slate-500" data-id="element-2867" data-mp="8umyfvr9vebo">Out of stock</dt><dd className={`text-lg font-semibold tabular-nums ${out ? 'text-red-700' : ''}`} data-id="element-2868" data-mp="3wlfdatujf07">{out}</dd></button>
      </dl>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3" data-id="element-2869" data-mp="ilzqand2m4hp">
        <div className="relative min-w-[220px] flex-1" data-id="element-2870" data-mp="do7vnll1k49t">
          <label htmlFor="inv-q" className="sr-only" data-id="element-2871" data-mp="rmfpvlqp0rha">Search inventory</label>
          <SearchIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-2872" data-mp="nlvsqi64dnv5" />
          <input id="inv-q" value={query} onChange={e => setQuery(e.target.value)} placeholder="Item, name, supplier or location" className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm" data-id="element-2873" data-mp="upljbvx4ne4c" />
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Show" data-id="element-2874" data-mp="14lf3o1h38bwo">
          {([['all', 'All'], ['stock', 'Stock items'], ['low', 'Low'], ['out', 'Out of stock'], ['service', 'Services']] as const).map(([id, label]) => <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)} className={`rounded-md px-3 py-1.5 text-sm font-medium ${filter === id ? 'bg-indigo-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`} data-id="element-2875" data-mp="1dho0r9hleyfn">{label}</button>)}
        </div>
      </div>

      {message && <p role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-id="element-2876" data-mp="3p4uhffpggvsf">{message}</p>}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-id="element-2877" data-mp="3u4yyp7y6tujp">
        {shown.length === 0 ? <div className="py-14 text-center text-sm text-slate-500" data-id="element-2878" data-mp="2badzavwzon6d">
            {items.length ? 'No items match.' : 'No items yet.'}
            {manage && !items.length && <div className="mt-3" data-id="element-2879" data-mp="22fzmczzxloqf"><Button size="sm" onClick={() => setAdding(true)} data-id="element-2880" data-mp="22hkdefn1epjj">Add the first item</Button></div>}
          </div> : <div className="overflow-x-auto" data-id="element-2881" data-mp="1xj9549ce4n3f">
            <table className="min-w-full text-sm" data-id="element-2882" data-mp="2u8727y7m4x3r">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-2883" data-mp="2u6vyfye63pcn">
                <tr data-id="element-2884" data-mp="2q494nrxxruct"><th className="px-3 py-2" data-id="element-2885" data-mp="2l70i7f5r945r">Item</th><th className="px-3 py-2" data-id="element-2886" data-mp="2l67iuz94lgxn">Description</th><th className="px-3 py-2 text-right" data-id="element-2887" data-mp="2l55yg467zi39">On hand</th><th className="px-3 py-2 text-right" data-id="element-2888" data-mp="2g8fhlemlcigs">Reorder</th><th className="px-3 py-2 text-right" data-id="element-2889" data-mp="2g7nxp12cw96c">Unit price</th><th className="px-3 py-2 text-right" data-id="element-2890" data-mp="19sd8mywzp3pu">Value at cost</th><th className="px-3 py-2" data-id="element-2891" data-mp="19pqz4tqjge4z">Status</th><th className="px-3 py-2" data-id="element-2892" data-mp="14t0nqjpoi6pw"><span className="sr-only" data-id="element-2893" data-mp="14rpgg9x0ygu1">Actions</span></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2894" data-mp="34uqnzdx4ar5w">
                {shown.map(r => {
              const own = r.item.kind === 'Stock';
              return <tr key={r.item.id} className="hover:bg-slate-50" data-id="element-2895" data-mp="2ux2hxru29x8f">
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs font-semibold" data-id="element-2896" data-mp="2uyx0x0lrvf9v">{r.item.code}</td>
                      <td className="px-3 py-2" data-id="element-2897" data-mp="3nlhrvzyc5pcw"><span className="block font-medium text-slate-900" data-id="element-2898" data-mp="3nl81wcdaji77">{r.item.name}</span><span className="text-xs text-slate-500" data-id="element-2899" data-mp="3skkzns5pa9ok">{[r.item.supplier, r.item.location].filter(Boolean).join(' · ') || r.item.description}</span></td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums" data-id="element-2900" data-mp="3dv5b8itn6gc1">{r.onHand === null ? '—' : `${r.onHand} ${r.item.unit ?? ''}`}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600" data-id="element-2901" data-mp="aepscdrmt1ow">{own ? r.item.reorderLevel ?? '—' : '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums" data-id="element-2902" data-mp="f6zgppd3ltp2">{fmt(r.item.unitPrice)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums" data-id="element-2903" data-mp="1zu0mmvou2kok">{r.onHand === null ? '—' : r.item.costPrice === undefined ? <span className="text-xs text-slate-400" data-id="element-2904" data-mp="29ln3b7jxxbyp">No cost</span> : fmt(r.value)}</td>
                      <td className="px-3 py-2" data-id="element-2905" data-mp="24n24nckc70cd"><span className={`rounded px-2 py-0.5 text-xs font-medium ${tone[r.state]}`} data-id="element-2906" data-mp="24nuzpg9xo1eo">{r.state}</span></td>
                      <td className="whitespace-nowrap px-3 py-2 text-right" data-id="element-2907" data-mp="1gxr8k8dxlu4k">
                        <div className="inline-flex gap-1" data-id="element-2908" data-mp="1bz69icaf1ghp">
                          {own && manage && <Button size="sm" variant="ghost" onClick={() => setAdjust({
                      item: r.item,
                      mode: 'Received'
                    })} data-id="element-2909" data-mp="1pyfcga9m2e3q">Receive</Button>}
                          {own && manage && <Button size="sm" variant="ghost" onClick={() => setAdjust({
                      item: r.item,
                      mode: 'Adjustment'
                    })} data-id="element-2910" data-mp="1lyez6sy908y9">Adjust</Button>}
                          {own && <Button size="sm" variant="ghost" onClick={() => setHistory(r.item)} data-id="element-2911" data-mp="2xc8zo1kl17sv">History</Button>}
                          {manage && <Button size="sm" variant="ghost" onClick={() => setEditing(r.item)} data-id="element-2912" data-mp="2kp64vqi6mxb0">Edit</Button>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {(adding || editing) && <ItemFormModal item={editing ?? undefined} onClose={() => {
      setAdding(false);
      setEditing(null);
    }} onSaved={m => {
      setAdding(false);
      setEditing(null);
      setMessage(m ?? 'Saved.');
    }} data-id="element-2913" data-mp="26ysyhskxvqns" />}
      {adjust && <StockAdjustModal item={adjust.item} mode={adjust.mode} onClose={() => setAdjust(null)} onDone={m => {
      setAdjust(null);
      setMessage(m);
    }} data-id="element-2914" data-mp="1x2zn4ciyi37l" />}
      {history && <Dialog title={`Stock history — ${history.name}`} subtitle={`${history.code} · ${history.quantityOnHand ?? 0} ${history.unit ?? ''} on hand`} onClose={() => setHistory(null)} size="lg" data-id="element-2915" data-mp="1o1i8yzp40blj">
          {itemMoves.length === 0 ? <p className="py-8 text-center text-sm text-slate-500" data-id="element-2916" data-mp="3iezmruzppu3d">No stock movements yet.</p> : <table className="min-w-full text-sm" data-id="element-2917" data-mp="3pxh1qgqcy13q">
              <thead className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-2918" data-mp="3uwae25twrm2b"><tr data-id="element-2919" data-mp="3hmjylbung6o"><th className="py-2 pr-3" data-id="element-2920" data-mp="3hmliprhzo8s">When</th><th className="py-2 pr-3" data-id="element-2921" data-mp="3jh96bu5lugk">Movement</th><th className="py-2 pr-3 text-right" data-id="element-2922" data-mp="3kilejmwomzv">Qty</th><th className="py-2 pr-3 text-right" data-id="element-2923" data-mp="8g7k6kr3pzv4">Balance</th><th className="py-2" data-id="element-2924" data-mp="8j26khtodur6">By · reason</th></tr></thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2925" data-mp="3706cloyfwo20">
                {itemMoves.map(m => <tr key={m.id} className="align-top" data-id="element-2926" data-mp="3byhos6c7jane">
                    <td className="whitespace-nowrap py-2 pr-3 text-xs text-slate-500" data-id="element-2927" data-mp="3gxauhcl95xd0">{formatDateTime(m.at)}</td>
                    <td className="py-2 pr-3" data-id="element-2928" data-mp="3kywav7wzwtry">{m.type}{m.reference && <span className="block text-xs text-slate-500" data-id="element-2929" data-mp="3kwjmabwqfl5x">{m.reference}</span>}</td>
                    <td className={`py-2 pr-3 text-right font-semibold tabular-nums ${m.quantity < 0 ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-2930" data-mp="w6jz4fkrkwv7">{m.quantity > 0 ? `+${m.quantity}` : m.quantity}</td>
                    <td className="py-2 pr-3 text-right tabular-nums" data-id="element-2931" data-mp="2x78751s1o20c">{m.after}</td>
                    <td className="py-2 text-xs text-slate-600" data-id="element-2932" data-mp="2s96q0me6icri">{m.byName}{m.reason && <span className="block italic" data-id="element-2933" data-mp="36a9bawxcnj1d">{m.reason}</span>}</td>
                  </tr>)}
              </tbody>
            </table>}
        </Dialog>}
    </div>;
}